import { useEffect, useRef, useState } from 'react';

export interface CascadeToastState {
  id: string;
  message: string;
  /** Provided when an Undo is offered. Click → caller restores prior lanes. */
  onUndo?: () => Promise<void> | void;
  /** Lifetime in ms before auto-dismiss. Defaults to 6000. */
  durationMs?: number;
}

interface Props {
  toast: CascadeToastState | null;
  onDismiss: () => void;
}

/**
 * Single-slot toast for cascade results. We keep the surface intentionally
 * scoped to the Todos workspace so we don't introduce an app-wide toast
 * system in this PRD.
 */
export function CascadeUndoToast({ toast, onDismiss }: Props) {
  const [busy, setBusy] = useState(false);
  const dismissedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    if (dismissedRef.current === toast.id) return;
    const ms = toast.durationMs ?? 6000;
    const timer = window.setTimeout(() => {
      dismissedRef.current = toast.id;
      onDismiss();
    }, ms);
    return () => window.clearTimeout(timer);
  }, [toast, onDismiss]);

  if (!toast) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 -translate-x-1/2 z-50 flank-card-elevated"
      style={{
        bottom: 24,
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: 14,
        padding: '10px 14px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.18)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        maxWidth: 'min(560px, calc(100vw - 32px))',
      }}
    >
      <span className="text-sm text-[var(--color-text-primary)]">{toast.message}</span>
      {toast.onUndo ? (
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            if (!toast.onUndo) return;
            setBusy(true);
            try {
              await toast.onUndo();
            } finally {
              setBusy(false);
              dismissedRef.current = toast.id;
              onDismiss();
            }
          }}
          className="flank-btn flank-btn-secondary text-xs px-3 py-1 disabled:opacity-60"
        >
          {busy ? 'Undoing…' : 'Undo'}
        </button>
      ) : null}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => {
          dismissedRef.current = toast.id;
          onDismiss();
        }}
        className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] text-xs px-2"
      >
        ✕
      </button>
    </div>
  );
}
